import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FiUploadCloud, FiSearch, FiTrash2, FiCopy, FiCheck, FiExternalLink,
  FiImage, FiChevronLeft, FiChevronRight, FiFolder, FiAlertTriangle,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, Spinner, Modal, ConfirmDialog, Badge } from '../../components/common';
import { mediaService, getApiError } from '../../services/cmsService';
import { formatFileSize } from '../../utils/fileUtils';
import { compressImage, isCompressibleImage } from '../../utils/imageCompressor';
import { formatDate } from '../../utils/dateUtils';
import { useDebounce } from '../../hooks';
import './MediaLibraryPage.css';

const PER_PAGE = 24;
const ACCEPTED = 'image/jpeg,image/jpg,image/png,image/webp,image/svg+xml';
const MAX_SIZE_MB = 5;
const MAX_ORIGINAL_MB = 30;

const FOLDERS = [
  { value: '', label: 'All folders' },
  { value: 'cms', label: 'CMS' },
  { value: 'settings', label: 'Settings' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'blogs', label: 'Blogs' },
  { value: 'news', label: 'News' },
];

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API needs a secure context; fall back to a hidden textarea
    try {
      const area = document.createElement('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(area);
      return ok;
    } catch {
      return false;
    }
  }
};

const MediaLibraryPage = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [folder, setFolder] = useState('cms');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, count: 0, percent: 0 });
  const [dragActive, setDragActive] = useState(false);

  const [details, setDetails] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const fileInputRef = useRef(null);
  const debouncedSearch = useDebounce(search, 350);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await mediaService.getAll({ page, limit: PER_PAGE, search: debouncedSearch, folder });
      setMedia(response.data.media);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      setMedia([]);
      setLoadError(getApiError(error, 'Failed to load the media library'));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, folder]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, folder]);

  const uploadFiles = async (files) => {
    const list = Array.from(files).filter((file) => {
      if (!ACCEPTED.split(',').includes(file.type)) {
        toast.error(`${file.name}: only JPG, PNG, WEBP and SVG are allowed`);
        return false;
      }
      // Photos are compressed below, so only refuse what is too big to decode or would stay too big
      const limitMB = isCompressibleImage(file) ? MAX_ORIGINAL_MB : MAX_SIZE_MB;
      if (file.size > limitMB * 1024 * 1024) {
        toast.error(`${file.name}: larger than ${limitMB}MB`);
        return false;
      }
      return true;
    });

    if (!list.length) return;

    setUploading(true);
    let succeeded = 0;

    // Sequential uploads keep the progress readout honest and go easy on the API
    for (let index = 0; index < list.length; index += 1) {
      setUploadProgress({ done: index, count: list.length, percent: 0 });
      try {
        // eslint-disable-next-line no-await-in-loop
        const file = await compressImage(list[index]);
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
          throw new Error(`still larger than ${MAX_SIZE_MB}MB after compression`);
        }
        // eslint-disable-next-line no-await-in-loop
        await mediaService.upload(file, {
          folder: folder || 'cms',
          onProgress: (percent) => setUploadProgress({ done: index, count: list.length, percent }),
        });
        succeeded += 1;
      } catch (error) {
        toast.error(`${list[index].name}: ${getApiError(error, 'upload failed')}`);
      }
    }

    setUploading(false);
    setUploadProgress({ done: 0, count: 0, percent: 0 });
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (succeeded) {
      toast.success(`${succeeded} ${succeeded === 1 ? 'image' : 'images'} uploaded`);
      setPage(1);
      fetchMedia();
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await mediaService.delete(deleteTarget.id);
      toast.success('Image deleted');
      if (details?.id === deleteTarget.id) setDetails(null);
      fetchMedia();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to delete the image'));
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleCopy = async (item) => {
    const ok = await copyToClipboard(item.url);
    if (ok) {
      setCopiedId(item.id);
      setTimeout(() => setCopiedId((current) => (current === item.id ? null : current)), 1600);
    } else {
      toast.error('Could not copy the URL');
    }
  };

  return (
    <div className="media-library-page">
      <Card
        title="Media Library"
        subtitle={`${total} ${total === 1 ? 'file' : 'files'} · reuse these images anywhere in the CMS`}
        action={
          <Button icon={<FiUploadCloud />} onClick={() => fileInputRef.current?.click()} loading={uploading}>
            Upload images
          </Button>
        }
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED}
          multiple
          hidden
          onChange={(e) => e.target.files?.length && uploadFiles(e.target.files)}
        />

        <div className="ml-toolbar">
          <div className="ml-search">
            <FiSearch />
            <input
              type="text"
              value={search}
              placeholder="Search by file name…"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="ml-folder">
            <FiFolder />
            <select value={folder} onChange={(e) => setFolder(e.target.value)} aria-label="Filter by folder">
              {FOLDERS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>

        {uploading && (
          <div className="ml-uploading">
            <Spinner size="small" />
            <span>
              Uploading {uploadProgress.done + 1} of {uploadProgress.count} — {uploadProgress.percent}%
            </span>
            <div className="ml-progress"><span style={{ width: `${uploadProgress.percent}%` }} /></div>
          </div>
        )}

        <div
          className={`ml-dropzone ${dragActive ? 'active' : ''}`}
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
          }}
        >
          {loading ? (
            <div className="ml-state"><Spinner size="medium" /></div>
          ) : loadError ? (
            <div className="ml-state ml-state--error">
              <FiAlertTriangle />
              <p>{loadError}</p>
              <Button variant="outline" size="small" onClick={fetchMedia}>Try again</Button>
            </div>
          ) : media.length === 0 ? (
            <div className="ml-state">
              <FiImage />
              <p>{debouncedSearch ? 'No images match that search.' : 'Nothing here yet — drop images anywhere in this area to upload.'}</p>
              <Button variant="outline" size="small" icon={<FiUploadCloud />} onClick={() => fileInputRef.current?.click()}>
                Upload images
              </Button>
            </div>
          ) : (
            <div className="ml-grid">
              {media.map((item) => (
                <figure key={item.id} className="ml-card">
                  <button type="button" className="ml-thumb" onClick={() => setDetails(item)} title={item.originalName}>
                    <img src={item.url} alt={item.altText || item.originalName} loading="lazy" />
                  </button>
                  <figcaption className="ml-card-body">
                    <span className="ml-name" title={item.originalName}>{item.originalName || 'Untitled'}</span>
                    <span className="ml-meta">
                      {formatFileSize(item.size)}
                      {item.width && item.height ? ` · ${item.width}×${item.height}` : ''}
                    </span>
                  </figcaption>
                  <div className="ml-card-actions">
                    <button type="button" className="ml-act" title="Copy URL" onClick={() => handleCopy(item)}>
                      {copiedId === item.id ? <FiCheck className="ok" /> : <FiCopy />}
                    </button>
                    <a className="ml-act" href={item.url} target="_blank" rel="noreferrer" title="Open in a new tab">
                      <FiExternalLink />
                    </a>
                    <button type="button" className="ml-act danger" title="Delete" onClick={() => setDeleteTarget(item)}>
                      <FiTrash2 />
                    </button>
                  </div>
                </figure>
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="ml-pager">
            <Button variant="ghost" size="small" icon={<FiChevronLeft />} disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <span className="ml-pager-info">Page {page} of {totalPages}</span>
            <Button variant="ghost" size="small" icon={<FiChevronRight />} iconPosition="right" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        )}
      </Card>

      {/* Details */}
      <Modal
        isOpen={Boolean(details)}
        onClose={() => setDetails(null)}
        title="Image details"
        size="large"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDetails(null)}>Close</Button>
            <Button variant="danger" icon={<FiTrash2 />} onClick={() => setDeleteTarget(details)}>Delete</Button>
          </>
        }
      >
        {details && (
          <div className="ml-details">
            <div className="ml-details-preview">
              <img src={details.url} alt={details.altText || details.originalName} />
            </div>
            <dl className="ml-details-list">
              <div><dt>File name</dt><dd>{details.originalName || '—'}</dd></div>
              <div><dt>Folder</dt><dd><Badge variant="default" size="small">{details.folder || 'cms'}</Badge></dd></div>
              <div><dt>Type</dt><dd>{details.mimeType || '—'}</dd></div>
              <div><dt>Size</dt><dd>{formatFileSize(details.size)}</dd></div>
              <div><dt>Dimensions</dt><dd>{details.width && details.height ? `${details.width} × ${details.height}px` : '—'}</dd></div>
              <div><dt>Alt text</dt><dd>{details.altText || '—'}</dd></div>
              <div><dt>Uploaded</dt><dd>{details.createdAt ? formatDate(details.createdAt) : '—'}</dd></div>
              <div className="ml-details-url">
                <dt>URL</dt>
                <dd>
                  <code>{details.url}</code>
                  <Button size="small" variant="outline" icon={copiedId === details.id ? <FiCheck /> : <FiCopy />} onClick={() => handleCopy(details)}>
                    {copiedId === details.id ? 'Copied' : 'Copy'}
                  </Button>
                </dd>
              </div>
            </dl>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Image"
        message={`Delete "${deleteTarget?.originalName}"? The file is removed from storage, and any page still pointing at it will show a broken image.`}
        confirmText="Delete image"
        loading={deleting}
      />
    </div>
  );
};

export default MediaLibraryPage;
